
import React, { useState, useEffect } from 'react';
import { QuoteData, BillingCadence, FeeType } from './types';
import { QuoteForm } from './components/QuoteForm';
import { QuotePreview } from './components/QuotePreview';
import { DownloadIcon, SparklesIcon } from './components/Icons';
import { generateProfessionalIntro } from './services/geminiService';

const INITIAL_QUOTE: QuoteData = {
  id: '2024-' + Math.floor(Math.random() * 9000 + 1000).toString(),
  date: new Date().toISOString().split('T')[0],
  validUntil: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  client: {
    companyName: '',
    contactPerson: '',
    email: '',
    address: ''
  },
  sender: {
    name: 'Ditt Namn',
    phone: '070-000 00 00',
    email: 'info@frostpil.se'
  },
  items: [
    {
      id: 'init-1',
      name: 'Digital Strategi & Analys',
      description: 'Kartläggning av nuvarande digitala närvaro och identifiering av tillväxtpotential.',
      price: 12000,
      quantity: 1,
      type: FeeType.FIXED
    }
  ],
  billingCadence: BillingCadence.MONTHLY,
  notes: '',
  momsRate: 0.25
};

export default function App() {
  const [quote, setQuote] = useState<QuoteData>(INITIAL_QUOTE);
  const [isGeneratingIntro, setIsGeneratingIntro] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  const handleGenerateIntro = async () => {
    if (!quote.client.companyName) {
      alert("Vänligen fyll i kundens företagsnamn först.");
      return;
    }
    setIsGeneratingIntro(true);
    const intro = await generateProfessionalIntro(
      quote.client.companyName, 
      quote.items.map(i => i.name)
    );
    setQuote(prev => ({ ...prev, notes: intro }));
    setIsGeneratingIntro(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 no-print">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://frostpil.pages.dev/assets/frostpil-logo-EFFG_rCZ.png" 
              alt="Frostpil Logo" 
              className="h-8 w-auto object-contain"
            />
            <div className="h-6 w-px bg-slate-200 mx-1"></div>
            <h1 className="text-sm font-bold brand-font text-slate-400 uppercase tracking-widest">Offertbyggare</h1>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={handlePrint}
              className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-all shadow-lg hover:shadow-xl active:scale-95"
            >
              <DownloadIcon className="w-4 h-4" /> Exportera PDF
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow max-w-7xl mx-auto w-full px-4 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Editor Side */}
        <div className="no-print space-y-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold text-slate-800">Skapa Offert</h2>
            <button
              onClick={handleGenerateIntro}
              disabled={isGeneratingIntro}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200 transition-colors"
            >
              <SparklesIcon className={`w-3 h-3 ${isGeneratingIntro ? 'animate-spin' : ''}`} />
              {isGeneratingIntro ? 'Genererar...' : 'Generera Inledningstext'}
            </button>
          </div>
          
          <QuoteForm quote={quote} setQuote={setQuote} />
          
          <div className="bg-blue-50 border border-blue-100 p-6 rounded-2xl">
            <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-2">Offertnoteringar / Inledning</h4>
            <textarea
              className="w-full h-32 p-3 bg-white border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm leading-relaxed"
              value={quote.notes}
              onChange={(e) => setQuote(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Skriv en inledning till kunden här..."
            />
          </div>
        </div>

        {/* Preview Side */}
        <div className="relative">
          <div className="lg:sticky lg:top-24">
             <div className="no-print mb-4 flex justify-between items-center">
               <h2 className="text-2xl font-bold text-slate-800">Förhandsgranskning</h2>
               <span className="text-xs font-medium text-slate-400">Uppdateras live</span>
             </div>
             <QuotePreview quote={quote} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-6 no-print">
        <div className="max-w-7xl mx-auto px-4 text-center text-slate-400 text-sm">
          &copy; {new Date().getFullYear()} Frostpil. Alla rättigheter reserverade.
        </div>
      </footer>
    </div>
  );
}
